import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Map, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { ProcessBlueprint, ProcessBlueprintLog, ProcessBlueprintUsage, ProcessBlueprintVersion, GeneratedMapData } from '../../../../types/processBlueprint';
import type { BlueprintListItem } from '../../services/blueprint.service';
import type { CardData, Connection } from '../../../../types/card';
import { getBlueprintById } from '../../services/blueprint.service';
import { getActiveExecutionsByVersion } from '../../services/governanceEvolution';
import { listGovernanceLogs } from '../../services/governance.service';
import { listUsageByBlueprint, listVersions } from '../../services/blueprintVersionUsageService';
import { useProjectStore } from '../../../../store/useProjectStore';
import { supabase } from '../../../../lib/supabaseClient';

interface BlueprintTechnicalDrawerProps {
  open: boolean;
  blueprintId: string | null;
  organizationId: string;
  summary?: BlueprintListItem | ProcessBlueprint | null;
  onClose: () => void;
}

interface DrawerState {
  blueprint: ProcessBlueprint | null;
  versions: ProcessBlueprintVersion[];
  usage: ProcessBlueprintUsage[];
  logs: ProcessBlueprintLog[];
  activeExecutions: number;
  loading: boolean;
  error: string | null;
}

function InfoRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="grid grid-cols-[96px_1fr] gap-2 text-[11px] leading-5">
      <span className="font-mono uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">{label}</span>
      <span className="truncate text-slate-700 dark:text-slate-200">{value}</span>
    </div>
  );
}

function DrawerSection({ title, children }: React.PropsWithChildren<{ title: string }>) {
  return (
    <section className="border-b border-slate-200/90 px-4 py-3 last:border-b-0 dark:border-slate-800/90">
      <h3 className="mb-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
        {title}
      </h3>
      <div className="space-y-1.5">{children}</div>
    </section>
  );
}

function formatDate(value?: string | null) {
  if (!value) return '-';
  return new Date(value).toLocaleString('pt-BR');
}

function formatCreatedBy(summary?: BlueprintListItem | ProcessBlueprint | null, blueprint?: ProcessBlueprint | null) {
  if (summary && 'createdByName' in summary && summary.createdByName) return summary.createdByName;
  if (blueprint?.createdByName) return blueprint.createdByName;
  if (blueprint?.createdBy) return blueprint.createdBy;
  return 'Sistema';
}

export function BlueprintTechnicalDrawer({
  open,
  blueprintId,
  organizationId,
  summary,
  onClose,
}: BlueprintTechnicalDrawerProps) {
  const [state, setState] = useState<DrawerState>({
    blueprint: null,
    versions: [],
    usage: [],
    logs: [],
    activeExecutions: 0,
    loading: false,
    error: null,
  });

  useEffect(() => {
    if (!open || !blueprintId) {
      setState({
        blueprint: null,
        versions: [],
        usage: [],
        logs: [],
        activeExecutions: 0,
        loading: false,
        error: null,
      });
      return;
    }

    let cancelled = false;
    setState((current) => ({ ...current, loading: true, error: null }));

    void Promise.all([
      getBlueprintById(blueprintId, organizationId),
      listVersions(blueprintId),
      listUsageByBlueprint(blueprintId),
      listGovernanceLogs(blueprintId, organizationId).catch(() => []),
      getActiveExecutionsByVersion(blueprintId, organizationId).catch(() => []),
    ])
      .then(([blueprint, versions, usage, logs, executionCounts]) => {
        if (cancelled) return;
        setState({
          blueprint,
          versions,
          usage,
          logs,
          activeExecutions: executionCounts.reduce((total, item) => total + item.activeCount, 0),
          loading: false,
          error: blueprint ? null : 'Blueprint nao encontrado.',
        });
      })
      .catch((error: Error) => {
        if (cancelled) return;
        setState({
          blueprint: null,
          versions: [],
          usage: [],
          logs: [],
          activeExecutions: 0,
          loading: false,
          error: error.message,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [open, blueprintId, organizationId]);

  const blueprint = state.blueprint;
  const displayName = summary?.name ?? blueprint?.name ?? 'Blueprint';

  const structureStats = useMemo(() => {
    const stages = blueprint?.stages ?? [];
    return {
      stageCount: stages.length,
      fieldCount: stages.reduce((total, stage) => total + (stage.formFields?.length ?? 0), 0),
      roleCount: new Set(stages.map((stage) => stage.role).filter(Boolean)).size,
    };
  }, [blueprint]);

  const usageStats = useMemo(() => {
    const linkedProjects = new Set(state.usage.map((item) => item.projectId)).size;
    return {
      linkedProjects,
      snapshots: state.versions.filter((item) => item.snapshotJson).length,
    };
  }, [state.usage, state.versions]);

  const navigate = useNavigate();
  const addSheet = useProjectStore((s) => s.addSheet);
  const setCards = useProjectStore((s) => s.setCards);
  const setCurrentBlueprintId = useProjectStore((s) => s.setCurrentBlueprintId);
  const setCurrentProjectId = useProjectStore((s) => s.setCurrentProjectId);

  const handleOpenMap = useCallback(() => {
    const mapData = blueprint?.generatedMapData;
    if (!mapData) return;

    // Store blueprintId so MapaPage can find the template link
    if (blueprint?.id) {
      setCurrentBlueprintId(blueprint.id);
      // Also look up and set the linked project
      supabase
        .from('process_blueprint_usage')
        .select('project_id')
        .eq('blueprint_id', blueprint.id)
        .limit(1)
        .maybeSingle()
        .then(({ data }) => {
          if (data?.project_id) setCurrentProjectId(data.project_id);
        });
    }

    const connsBySrc = new window.Map<string, Connection[]>();
    for (const conn of mapData.connections) {
      if (!connsBySrc.has(conn.sourceId)) connsBySrc.set(conn.sourceId, []);
      connsBySrc.get(conn.sourceId)!.push({
        id: crypto.randomUUID(),
        targetId: conn.targetId,
        sourceHandle: 'right',
      });
    }

    const normalizedCards: CardData[] = mapData.cards.map((card) => ({
      id: card.id,
      tipo: card.tipo as CardData['tipo'],
      atividade: card.atividade || card.nome_fluxo || '',
      nome_fluxo: card.nome_fluxo,
      posicao: card.posicao ?? { x: 100, y: 200 },
      status: 'ativo' as const,
      tp: card.tp,
      lt: card.lt,
      tt: card.tt,
      traUnit: card.traUnit as CardData['traUnit'],
      entrada: card.entrada,
      sector: card.sector || card.setor,
      responsavel: card.responsavel || card.quem,
      pessoasSimultaneas: card.pessoasSimultaneas,
      resources: card.resources,
      quem: card.quem,
      conexoes_saida: connsBySrc.get(card.id) ?? [],
      ...(card.executionConfig ? { executionConfig: card.executionConfig } : {}),
    }));

    addSheet(blueprint?.name ?? 'Blueprint');
    setCards(normalizedCards);
    onClose();
    navigate('/mapa');
  }, [blueprint, addSheet, setCards, navigate, onClose, setCurrentBlueprintId, setCurrentProjectId]);

  if (!open || !blueprintId) return null;

  return (
    <aside className="flex h-full w-[368px] shrink-0 flex-col border-l border-slate-300/80 bg-slate-50 dark:border-slate-800 dark:bg-[var(--bg-workspace)]">
      <div className="flex items-start justify-between border-b border-slate-300/80 px-4 py-3 dark:border-slate-800">
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">
            Blueprint selecionado
          </p>
          <h2 className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{displayName}</h2>
          <p className="text-[10px] text-slate-500 dark:text-slate-400">
            Painel tecnico de metadados e rastreabilidade
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-[3px] p-1 text-slate-500 transition-colors hover:bg-slate-200/70 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
        >
          <X size={14} />
        </button>
      </div>

      {state.loading ? (
        <div className="flex flex-1 items-center justify-center gap-2 text-[11px] text-slate-500 dark:text-slate-400">
          <Loader2 size={14} className="animate-spin" />
          <span>Carregando metadados</span>
        </div>
      ) : state.error ? (
        <div className="flex flex-1 items-center justify-center px-6 text-center text-[11px] text-red-600 dark:text-red-400">
          {state.error}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          <DrawerSection title="Metadados">
            <InfoRow label="Blueprint ID" value={blueprint?.id ?? blueprintId} />
            <InfoRow label="Template ID" value={blueprint?.generatedTemplateId ?? '-'} />
            <InfoRow label="Semver" value={blueprint?.versionSemver ?? (summary as BlueprintListItem | undefined)?.versionSemver ?? '-'} />
            <InfoRow label="Criado em" value={formatDate(blueprint?.createdAt)} />
            <InfoRow label="Atualizado" value={formatDate(blueprint?.updatedAt)} />
            <InfoRow label="Criado por" value={formatCreatedBy(summary, blueprint)} />
          </DrawerSection>

          <DrawerSection title="Estrutura">
            <InfoRow label="Qtd etapas" value={structureStats.stageCount} />
            <InfoRow label="Qtd campos" value={structureStats.fieldCount} />
            <InfoRow label="Qtd papeis" value={structureStats.roleCount} />
          </DrawerSection>

          {blueprint?.generatedMapData && (
            <DrawerSection title="Mapa do Processo">
              <BlueprintMapMini mapData={blueprint.generatedMapData} />
              <button
                type="button"
                onClick={handleOpenMap}
                className="mt-2 w-full rounded-[3px] border border-purple-300 bg-purple-50 px-3 py-1.5 text-[11px] font-medium text-purple-700 transition-colors hover:bg-purple-100 dark:border-purple-800 dark:bg-purple-950/40 dark:text-purple-300 dark:hover:bg-purple-900/50"
              >
                <Map size={12} className="mr-1 inline-block" />
                Abrir no Editor de Mapas
              </button>
            </DrawerSection>
          )}

          <DrawerSection title="Uso">
            <InfoRow label="Instancias" value={state.activeExecutions} />
            <InfoRow label="Projetos" value={usageStats.linkedProjects} />
          </DrawerSection>

          <DrawerSection title="Historico">
            <InfoRow label="Versoes" value={state.versions.length} />
            <InfoRow label="Snapshots" value={usageStats.snapshots} />
            <div className="pt-1">
              {state.versions.length === 0 ? (
                <p className="text-[11px] text-slate-500 dark:text-slate-400">Nenhum snapshot registrado.</p>
              ) : (
                <div className="space-y-1.5">
                  {state.versions.slice(0, 6).map((version) => (
                    <div
                      key={version.id}
                      className="rounded-[3px] border border-slate-200 bg-white px-2.5 py-2 text-[11px] dark:border-slate-800 dark:bg-slate-900"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-mono text-slate-700 dark:text-slate-200">v{version.versionSemver}</span>
                        <span className="text-slate-400 dark:text-slate-500">{formatDate(version.createdAt)}</span>
                      </div>
                      {version.changeSummary ? (
                        <p className="mt-1 truncate text-slate-500 dark:text-slate-400">{version.changeSummary}</p>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DrawerSection>

          <DrawerSection title="Auditoria recente">
            {state.logs.length === 0 ? (
              <p className="text-[11px] text-slate-500 dark:text-slate-400">Sem eventos registrados.</p>
            ) : (
              <div className="space-y-0.5">
                {state.logs.slice(0, 5).map((log) => (
                  <div
                    key={log.id}
                    className="relative pl-4 before:absolute before:left-[2px] before:top-0 before:bottom-0 before:w-px before:bg-slate-200 dark:before:bg-slate-800"
                  >
                    <span className="absolute left-0 top-[7px] h-[5px] w-[5px] rounded-full bg-slate-400 dark:bg-slate-500" />
                    <div className="flex items-start justify-between gap-3 py-1">
                      <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-600 dark:text-slate-300">
                        {String(log.action).replace(/_/g, ' ')}
                      </span>
                      <span className="shrink-0 text-[10px] text-slate-400 dark:text-slate-500">{formatDate(log.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DrawerSection>
        </div>
      )}
    </aside>
  );
}

export default BlueprintTechnicalDrawer;

function BlueprintMapMini({ mapData }: { mapData: GeneratedMapData }) {
  const activityCards = mapData.cards.filter((c) => c.tipo === 'atividade');

  const formatTp = (minutes: number) => {
    if (minutes >= 1440) return `${Math.round(minutes / 1440)}d`;
    if (minutes >= 60) return `${Math.round(minutes / 60)}h`;
    return `${minutes}min`;
  };

  return (
    <div className="flex items-center gap-1 overflow-x-auto py-2 px-1 rounded-[3px] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900/50">
      <div className="flex-shrink-0 w-4 h-4 rounded-full bg-green-500/20 border border-green-500/40 flex items-center justify-center">
        <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
      </div>
      {activityCards.map((card) => (
        <div key={card.id} className="flex items-center gap-1">
          <span className="text-slate-400 text-[9px]">{'\u2192'}</span>
          <div
            className="flex-shrink-0 border border-slate-300 dark:border-slate-700 rounded-[2px] px-1 py-0.5 text-[9px] leading-tight bg-slate-50 dark:bg-slate-800 max-w-[72px] truncate"
            title={card.atividade}
          >
            <span className="block truncate">{card.atividade}</span>
            {card.tp != null && (
              <span className="block text-slate-400 text-[8px]">{formatTp(card.tp)}</span>
            )}
          </div>
        </div>
      ))}
      <span className="text-slate-400 text-[9px]">{'\u2192'}</span>
      <div className="flex-shrink-0 w-4 h-4 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center">
        <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
      </div>
    </div>
  );
}
